import { useQuery } from '@tanstack/react-query';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { ApiClient } from '../../../../api/ApiClient';
import { v2 } from '../../../../api/ApiInterfaceV2';
import { JiraFieldData2, JiraIssueData } from '../../../../api/endpoints/jira';
import { useDraft2, useObserverReturningStore } from '../../../../CodesThatsGottaGoSomewhereElse';
import { PodRecord } from '../../../../concepts/objects/RecordSetManager';
import { tryConvert } from '../../../../platform/Convert';
import { Colors } from '../../../../ui';
import { SavingIndicator } from '../../../../ui/SavingIndicator';
import { FlexScroller } from '../../../../ui/Scrollers';
import { useResolve } from '../../../Container';
import { Flexer } from '../../../dashboard/elements/LayoutElements';
import { JiraFieldInput, JiraFieldInputProps } from '../../../external/atlassian/inputs/JiraFieldInput';
import { AtlassianContext } from '../AtlassianContext';
import { JiraIssueSidebarActions } from './JiraIssueSidebarActions';
import { useJiraIssueRecord } from './JiraIssueSidebarHooks';

const useJiraIssueEditSchemaQuery = (jiraIssueId: string) => {
    const api = useResolve(ApiClient);
    return useQuery({
        queryKey: ['jira.issues.editSchema', jiraIssueId],
        queryFn: () => api.exec(v2.jira.issues.editSchema({ jiraIssueId })),
    });
};

const DetailsGrid = styled.div`
    display: grid;
    grid-template-columns: 40% 1fr;
    gap: 1rem;
`;

const bannedFields = new Set([
    'summary',
    'description',
    'environment',
]);

const formatRecordPatch = (record: PodRecord<JiraIssueData>, field: JiraFieldData2, newValue: any) => {

    if (field.custom) {
        const customInfo = record.get('custom')?.[field.id];
        if (!customInfo) return {};
        newValue = tryConvert(newValue, customInfo.type) ?? newValue;
        return {
            custom: {
                [field.id]: {
                    ...customInfo,
                    value: newValue
                }
            }
        };
    }
    else {
        return {
            [field.id]: newValue
        };
    }
};

const JiraFieldPendingChangesIndicator = styled.div`
    position: absolute;
    top: 0;
    right: 0;
    color: ${Colors.textLighter};
    font-size: 0.875rem;
    font-style: italic;
`;

export const JiraIssueDetailsFieldInput = React.memo(({ onChange, onSubmit, ...rest }: JiraFieldInputProps) => {

    const [hasChanges, setHasChanges] = useState(false);

    const onChangeWrapped = useCallback((value: any) => {
        onChange(value);
        setHasChanges(true);
    }, [onChange]);

    const onSubmitWrapped = useCallback((value: any) => {
        onSubmit?.(value);
        setHasChanges(false);
    }, [onSubmit]);

    return <Flexer relative>
        <JiraFieldInput {...rest} onChange={onChangeWrapped} onSubmit={onSubmitWrapped} />
        {hasChanges && <JiraFieldPendingChangesIndicator>
            Pending Changes
        </JiraFieldPendingChangesIndicator>}
    </Flexer>;
});

export const JiraIssueDetailsView = React.memo(({ jiraIssueId }: { jiraIssueId: string }) => {

    const record = useObserverReturningStore(useJiraIssueRecord(jiraIssueId));

    const editSchema = useJiraIssueEditSchemaQuery(jiraIssueId);
    const atlassian = useResolve(AtlassianContext);
    const { draft, patchDraft, resetDraft } = useDraft2({} as Record<string, any>);

    const [activePatches, setActivePatches] = useState([] as string[]);
    const [patchCount, setPatchCount] = useState(0);

    useEffect(() => atlassian.on('patched', (ids: string[]) => {
        setActivePatches(ap => ap.filter(x => !ids.includes(x)));
    }), []);

    const onChange = ({ id }: JiraFieldData2) => (val: any) => {
        patchDraft({ [id]: val });
    };

    const onSubmit = (field: JiraFieldData2) => (val: any) => {
        const { id, custom } = field;
        patchDraft({ [id]: val });
        record.mutate(formatRecordPatch(record, field, val));
        const patchId = atlassian.queueIssuePatch({
            jiraIssueId,
            actions: [{
                type: 'set',
                params: { prop: id, value: val, custom }
            }]
        });
        setActivePatches(x => [...x, patchId]);
        setPatchCount(x => x + 1);
    };

    const editableFields = useMemo(() => {
        return editSchema.data?.filter(x => !bannedFields.has(x.id)) ?? [];
    }, [editSchema.data]);

    useEffect(() => {
        if (!editSchema?.data) return;
        resetDraft({
            ...Object.fromEntries(
                editableFields
                    ?.map(x => [x.id, (record.get(x.id as any) || record.get('custom')?.[x.id]?.value) || null])
                    ?.filter(x => !!x[1])
                ?? []
            ),
        });
    }, [record.getState(), editableFields]);

    return <Flexer grow flex="column" gap="1">
        <Flexer grow flex="column">
            <FlexScroller shadow>
                <Flexer flex="column" gap="1" padding="1">
                    {!editSchema.isLoading && editableFields.map(x =>
                        <Flexer key={x.id}>
                            <JiraIssueDetailsFieldInput field={x} value={draft[x.id]} onChange={onChange(x)} onSubmit={onSubmit(x)} />
                        </Flexer>
                    )}
                </Flexer>
            </FlexScroller>
        </Flexer>
        <JiraIssueSidebarActions jiraIssueId={jiraIssueId}>
            <SavingIndicator state={(
                (!!activePatches.length ? 'saving' : null) ??
                (!activePatches.length && !!patchCount ? 'success' : null) ??
                'idle'
            )} />
        </JiraIssueSidebarActions>
    </Flexer>;
});
